import {
  countConditions,
  type ListQuery,
  type ManualReturnCandidate,
  type Page,
  type ReceiveReturnInput,
  type ReturnLossRow,
  type ReturnParcel,
  type ReturnReceipt,
  type ReturnsRepository,
  type ReturnsSettings,
  type ReturnToReceive,
  type WriteResult,
} from "@estelle/domain";

import { API_BASE, ForbiddenError, apiFetch } from "@/adapters/api/base";

/**
 * Receiving returned parcels, over HTTP (#529).
 *
 * `apps/api/src/routes/returns.ts` is the other half of this file. A refusal
 * the server gives for an expected reason (a count that does not add up, a
 * parcel already received, a missing photo) arrives as a `WriteResult` body with
 * a 4xx or 503 status, a rejection to show; a 403 from the permission guard is a
 * `forbidden` rejection on a write and `ForbiddenError` on a read. Only a
 * transport failure throws, with the status and path and never a body: a
 * receipt names an order, and this message reaches a screen.
 *
 * Nothing here is written locally. Every answer is the server's, read back from
 * the receipt's own rows, and a screen replaces what it shows from it.
 */

const BASE = "/api/returns";

function params(query: ListQuery | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!query) return out;
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    out.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    out.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    out.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return out;
}

async function read<T>(path: string, query?: ListQuery): Promise<T> {
  const search = params(query).toString();
  const response = await apiFetch(
    `${BASE}${path}${search ? `?${search}` : ""}`,
  );
  if (response.status === 403) {
    const body = (await response.json().catch(() => null)) as {
      permission?: unknown;
    } | null;
    throw new ForbiddenError(
      `${BASE}${path}`,
      typeof body?.permission === "string" ? body.permission : undefined,
    );
  }
  if (!response.ok) {
    throw new Error(
      `Returns request failed: ${String(response.status)} on ${path}`,
    );
  }
  return (await response.json()) as T;
}

/** A write's outcome: a `WriteResult` body, or a 403 turned into a rejection. */
async function write<T>(
  path: string,
  init: RequestInit,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, init);
  const result = (await response.json().catch(() => null)) as
    WriteResult<T> | { permission?: unknown } | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  if (response.status === 403) {
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to do that.",
        permission:
          result !== null && typeof result.permission === "string"
            ? result.permission
            : "orders.returns.receive",
      },
    };
  }
  throw new Error(
    `Returns ${init.method ?? "GET"} failed: ${String(response.status)} on ${path}`,
  );
}

const json = (method: "POST" | "PATCH", body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

export function createApiReturns(): ReturnsRepository {
  return {
    listToReceive: (query) => read<Page<ReturnToReceive>>("/to-receive", query),

    findManualCandidates: async (search) =>
      (
        await read<{ items: ManualReturnCandidate[] }>("/manual/candidates", {
          search,
        })
      ).items,

    addManual: (shipmentId, reason) =>
      write<{ shipmentId: string }>(
        "/manual",
        json("POST", { shipmentId, reason }),
      ),

    getParcel: async (shipmentId): Promise<ReturnParcel | null> => {
      const response = await apiFetch(
        `${BASE}/parcels/${encodeURIComponent(shipmentId)}`,
      );
      if (response.status === 404) return null;
      if (response.status === 403) {
        throw new ForbiddenError(`${BASE}/parcels`, "orders.returns.receive");
      }
      if (!response.ok) {
        throw new Error(`Returns request failed: ${String(response.status)}`);
      }
      return (await response.json()) as ReturnParcel;
    },

    receive: (shipmentId, input: ReceiveReturnInput) => {
      // A form: the one shape that can carry photos. The counts travel as JSON
      // in `payload`, and each photo under `photo:<line id>`.
      const form = new FormData();
      form.set(
        "payload",
        JSON.stringify({
          warehouseId: input.warehouseId,
          note: input.note ?? null,
          lines: input.lines.map((line) => ({
            orderLineItemId: line.orderLineItemId,
            good: line.good,
            damaged: line.damaged,
            missing: line.missing,
            damagedReason: line.damagedReason ?? null,
            missingReason: line.missingReason ?? null,
          })),
        }),
      );
      for (const line of input.lines) {
        for (const photo of line.photos ?? []) {
          form.append(
            `photo:${line.orderLineItemId}`,
            new Blob([photo.data.slice()], { type: photo.contentType }),
            photo.filename,
          );
        }
      }
      return write<ReturnReceipt>(
        `/parcels/${encodeURIComponent(shipmentId)}/receive`,
        { method: "POST", body: form },
      );
    },

    retry: (receiptId) =>
      write<ReturnReceipt>(`/receipts/${encodeURIComponent(receiptId)}/retry`, {
        method: "POST",
      }),

    createFollowUp: (receiptId) =>
      write<{ taskId: string; created: boolean }>(
        `/receipts/${encodeURIComponent(receiptId)}/follow-up`,
        { method: "POST" },
      ),

    // The download route, never the bucket: it is private, and every fetch of
    // this URL is permission-checked on the way through. Absolute, because the
    // API is a different origin from the SPA in every deployed environment.
    photoUrl: (receiptId, photoId) =>
      `${API_BASE}${BASE}/receipts/${encodeURIComponent(receiptId)}/photos/${encodeURIComponent(photoId)}`,

    listLosses: (query) => read<Page<ReturnLossRow>>("/losses", query),

    getSettings: () => read<ReturnsSettings>("/settings"),

    saveSettings: (patch) =>
      write<ReturnsSettings>("/settings", json("PATCH", patch)),
  };
}
